/**
 * Country for a shop, derived from its `state`.
 *
 * There is no country column on `shops` — only city and state — so the only
 * way to group listings by country is to recognise the region. The stored
 * values are inconsistent by design (self-onboarding, seeding and the admin
 * tool all write differently), so both "IL" and "Illinois" have to resolve to
 * the same place, as do "CA" and "California".
 *
 * ONE COLLISION MATTERS: "CA" is California, not Canada. Canadian shops store
 * a province ("BC", "Ontario"), never "CA", so the US mapping wins the
 * two-letter form and Canada is matched by province name.
 *
 * Unknown regions return null rather than a guess — a shop under the wrong
 * flag is worse than a shop under no flag.
 */

export interface Country {
  code: string
  flag: string
  name: string
}

export const COUNTRIES: Record<string, Country> = {
  US: { code: "US", flag: "🇺🇸", name: "United States" },
  CA: { code: "CA", flag: "🇨🇦", name: "Canada" },
  MX: { code: "MX", flag: "🇲🇽", name: "Mexico" },
  PR: { code: "PR", flag: "🇵🇷", name: "Puerto Rico" },
  DO: { code: "DO", flag: "🇩🇴", name: "Dominican Republic" },
  JM: { code: "JM", flag: "🇯🇲", name: "Jamaica" },
  CR: { code: "CR", flag: "🇨🇷", name: "Costa Rica" },
  PA: { code: "PA", flag: "🇵🇦", name: "Panama" },
  GT: { code: "GT", flag: "🇬🇹", name: "Guatemala" },
  BR: { code: "BR", flag: "🇧🇷", name: "Brazil" },
  AR: { code: "AR", flag: "🇦🇷", name: "Argentina" },
  CL: { code: "CL", flag: "🇨🇱", name: "Chile" },
  CO: { code: "CO", flag: "🇨🇴", name: "Colombia" },
  PE: { code: "PE", flag: "🇵🇪", name: "Peru" },
  EC: { code: "EC", flag: "🇪🇨", name: "Ecuador" },
  UY: { code: "UY", flag: "🇺🇾", name: "Uruguay" },
  GB: { code: "GB", flag: "🇬🇧", name: "United Kingdom" },
  IE: { code: "IE", flag: "🇮🇪", name: "Ireland" },
  FR: { code: "FR", flag: "🇫🇷", name: "France" },
  ES: { code: "ES", flag: "🇪🇸", name: "Spain" },
  PT: { code: "PT", flag: "🇵🇹", name: "Portugal" },
  IT: { code: "IT", flag: "🇮🇹", name: "Italy" },
  DE: { code: "DE", flag: "🇩🇪", name: "Germany" },
  NL: { code: "NL", flag: "🇳🇱", name: "Netherlands" },
  BE: { code: "BE", flag: "🇧🇪", name: "Belgium" },
  CH: { code: "CH", flag: "🇨🇭", name: "Switzerland" },
  AT: { code: "AT", flag: "🇦🇹", name: "Austria" },
  SE: { code: "SE", flag: "🇸🇪", name: "Sweden" },
  NO: { code: "NO", flag: "🇳🇴", name: "Norway" },
  DK: { code: "DK", flag: "🇩🇰", name: "Denmark" },
  PL: { code: "PL", flag: "🇵🇱", name: "Poland" },
  CZ: { code: "CZ", flag: "🇨🇿", name: "Czechia" },
  GR: { code: "GR", flag: "🇬🇷", name: "Greece" },
  HR: { code: "HR", flag: "🇭🇷", name: "Croatia" },
  TR: { code: "TR", flag: "🇹🇷", name: "Türkiye" },
  RU: { code: "RU", flag: "🇷🇺", name: "Russia" },
  AE: { code: "AE", flag: "🇦🇪", name: "United Arab Emirates" },
  SA: { code: "SA", flag: "🇸🇦", name: "Saudi Arabia" },
  IL: { code: "IL", flag: "🇮🇱", name: "Israel" },
  EG: { code: "EG", flag: "🇪🇬", name: "Egypt" },
  MA: { code: "MA", flag: "🇲🇦", name: "Morocco" },
  NG: { code: "NG", flag: "🇳🇬", name: "Nigeria" },
  KE: { code: "KE", flag: "🇰🇪", name: "Kenya" },
  ZA: { code: "ZA", flag: "🇿🇦", name: "South Africa" },
  JP: { code: "JP", flag: "🇯🇵", name: "Japan" },
  KR: { code: "KR", flag: "🇰🇷", name: "South Korea" },
  CN: { code: "CN", flag: "🇨🇳", name: "China" },
  HK: { code: "HK", flag: "🇭🇰", name: "Hong Kong" },
  TW: { code: "TW", flag: "🇹🇼", name: "Taiwan" },
  SG: { code: "SG", flag: "🇸🇬", name: "Singapore" },
  TH: { code: "TH", flag: "🇹🇭", name: "Thailand" },
  VN: { code: "VN", flag: "🇻🇳", name: "Vietnam" },
  PH: { code: "PH", flag: "🇵🇭", name: "Philippines" },
  ID: { code: "ID", flag: "🇮🇩", name: "Indonesia" },
  MY: { code: "MY", flag: "🇲🇾", name: "Malaysia" },
  IN: { code: "IN", flag: "🇮🇳", name: "India" },
  AU: { code: "AU", flag: "🇦🇺", name: "Australia" },
  NZ: { code: "NZ", flag: "🇳🇿", name: "New Zealand" },
}

/**
 * Draw order for the flag row when a country has no listings to sort it by.
 * Grouped by region — North America, South America, Europe, Middle East &
 * Africa, Asia, Oceania — so scrolling reads like crossing a map rather than
 * an alphabet. Countries BOOTH already has shops in are pulled to the front
 * at render time.
 *
 * Every code here MUST also be imported in components/country-flag.tsx. A
 * missing import renders nothing at all rather than throwing, which is how
 * two dozen countries once vanished from the row while this list was right.
 */
export const COUNTRY_ROSTER: readonly string[] = [
  "US", "CA", "MX", "PR", "DO", "JM",
  "CR", "PA", "GT", "BR", "AR", "CL",
  "CO", "PE", "EC", "UY", "GB", "IE",
  "FR", "ES", "PT", "IT", "DE", "NL",
  "BE", "CH", "AT", "SE", "NO", "DK",
  "PL", "CZ", "GR", "HR", "TR", "RU",
  "AE", "SA", "IL", "EG", "MA", "NG",
  "KE", "ZA", "JP", "KR", "CN", "HK",
  "TW", "SG", "TH", "VN", "PH", "ID",
  "MY", "IN", "AU", "NZ",
]

const US_STATES = [
  "al","ak","az","ar","ca","co","ct","de","fl","ga","hi","id","il","in","ia","ks","ky","la","me","md",
  "ma","mi","mn","ms","mo","mt","ne","nv","nh","nj","nm","ny","nc","nd","oh","ok","or","pa","ri","sc",
  "sd","tn","tx","ut","vt","va","wa","wv","wi","wy","dc",
  "alabama","alaska","arizona","arkansas","california","colorado","connecticut","delaware","florida",
  "georgia","hawaii","idaho","illinois","indiana","iowa","kansas","kentucky","louisiana","maine",
  "maryland","massachusetts","michigan","minnesota","mississippi","missouri","montana","nebraska",
  "nevada","new hampshire","new jersey","new mexico","new york","north carolina","north dakota","ohio",
  "oklahoma","oregon","pennsylvania","rhode island","south carolina","south dakota","tennessee","texas",
  "utah","vermont","virginia","washington","west virginia","wisconsin","wyoming","district of columbia",
]

// Non-US regions, keyed on the lowercased stored value.
const REGION_TO_COUNTRY: Record<string, string> = {
  // Canada — provinces only. Never "CA", which is California above.
  "bc": "CA", "british columbia": "CA", "on": "CA", "ontario": "CA", "ab": "CA", "alberta": "CA",
  "qc": "CA", "quebec": "CA", "québec": "CA", "mb": "CA", "manitoba": "CA", "ns": "CA", "nova scotia": "CA",
  // Mexico
  "cdmx": "MX", "ciudad de méxico": "MX", "ciudad de mexico": "MX", "distrito federal": "MX",
  "estado de méxico": "MX", "estado de mexico": "MX", "jalisco": "MX", "nuevo león": "MX", "nuevo leon": "MX",
  // United Kingdom
  "greater london": "GB", "london": "GB", "england": "GB", "scotland": "GB", "wales": "GB",
  "northern ireland": "GB", "greater manchester": "GB", "west midlands": "GB", "merseyside": "GB",
  // Spain
  "catalonia": "ES", "cataluña": "ES", "catalunya": "ES", "madrid": "ES", "andalusia": "ES",
  "andalucía": "ES", "valencia": "ES",
  // Japan
  "tokyo": "JP", "osaka": "JP", "kyoto": "JP", "kanagawa": "JP", "hokkaido": "JP", "fukuoka": "JP",
  // Taiwan
  "taipei city": "TW", "taipei": "TW", "new taipei city": "TW", "taichung": "TW", "kaohsiung": "TW",
  // Australia
  "vic": "AU", "victoria": "AU", "nsw": "AU", "new south wales": "AU", "qld": "AU", "queensland": "AU",
  "wa australia": "AU", "sa": "AU", "south australia": "AU", "tas": "AU", "tasmania": "AU",
}

/** ISO code for a stored state/region value, or null when unrecognised. */
export function countryCodeForState(state: string | null | undefined): string | null {
  const key = (state || "").trim().toLowerCase()
  if (!key) return null
  // Non-US regions first: "victoria" and "london" would otherwise be ambiguous
  // against nothing, but keeping this order makes the intent explicit.
  if (REGION_TO_COUNTRY[key]) return REGION_TO_COUNTRY[key]
  if (US_STATES.includes(key)) return "US"
  return null
}

/** Full country record for a stored state/region value, or null. */
export function countryForState(state: string | null | undefined): Country | null {
  const code = countryCodeForState(state)
  return code ? COUNTRIES[code] ?? null : null
}

/**
 * Alternative spellings people actually type for a country. Search indexes
 * all of them, so "uk", "britain" and "united kingdom" reach the same shops,
 * and nobody has to guess which form BOOTH stored.
 */
const COUNTRY_SEARCH_ALIASES: Record<string, string[]> = {
  US: ["usa", "u.s.", "u.s.a.", "america", "united states of america", "states"],
  GB: ["uk", "u.k.", "britain", "great britain", "england", "scotland", "wales"],
  AE: ["uae", "dubai", "abu dhabi", "emirates"],
  KR: ["korea", "s korea"],
  TW: ["taiwan", "republic of china"],
  NL: ["holland", "the netherlands"],
  TR: ["turkey", "turkiye"],
  CZ: ["czech republic", "czechia"],
  MX: ["méxico"],
  DO: ["dr", "dominican rep"],
  PR: ["puerto rico", "pr"],
  HK: ["hong kong", "hk"],
  NZ: ["nz", "new zealand"],
  ZA: ["south africa", "rsa"],
}

/**
 * Lowercase strings a search should treat as "this country" — the name, the
 * ISO code, and the common alternatives above. Empty when the region is not
 * recognised, so an unknown state adds nothing rather than a wrong guess.
 */
export function countrySearchTerms(state: string | null | undefined): string[] {
  const country = countryForState(state)
  if (!country) return []
  return [
    country.name.toLowerCase(),
    country.code.toLowerCase(),
    ...(COUNTRY_SEARCH_ALIASES[country.code] ?? []),
  ]
}
